import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  MenuItem,
  TextField,
} from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import TextfieldDefault from "../../../../components/textField/TextfieldDefaultNew";
import { CustomTheme } from "../../../../types/customTheme";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import {
  createLicense,
  getAllLocations,
  getChargebeeSubscriptionsByCustomer,
} from "../../../../services/locationApp/licenseService";
import Cookies from "js-cookie";
import ChargebeeSubscriptionList, {
  ChargebeeSubscription,
} from "./ChargebeeSubscriptionList";

const getLicenseStatusFromSubscriptionStatus = (
  subscriptionStatus?: string
) => {
  const normalizedStatus = String(subscriptionStatus || "").trim().toLowerCase();

  const lockedStatuses = ["future", "paused", "cancelled", "transferred"];
  const activeStatuses = ["in_trial", "active", "non_renewing"];

  if (lockedStatuses.includes(normalizedStatus)) return "locked";
  if (activeStatuses.includes(normalizedStatus)) return "active";

  return null;
};

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    dialogPaper: {
      borderRadius: 10,
    },
    dropdown: {
      backgroundColor: theme.palette.background.entity_background,
      borderRadius: 10,
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: theme.palette.background.entity_background,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
    },
    dropdownMenuPaper: {
      backgroundColor: theme.palette.background.entity_background,
      borderRadius: 10,
      boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
      padding: theme.spacing(1),
    },
    errorText: {
      color: "#FF0000",
      fontSize: 12,
      marginTop: -8,
      marginBottom: 4,
    },
    helperText: {
      color: theme.palette.text.secondary,
      fontSize: 12,
      marginTop: -10,
    },
    duplicateErrorBox: {
      borderRadius: 10,
      border: "1px solid #FF0000",
      backgroundColor: "rgba(255, 0, 0, 0.06)",
      padding: theme.spacing(1.5),
      marginTop: -6,
    },
    duplicateErrorTitle: {
      color: theme.palette.error.main,
      fontWeight: 600,
      fontSize: 14,
      marginBottom: theme.spacing(1),
    },
    duplicateDetailText: {
      fontSize: 13,
      color: theme.palette.text.primary,
      marginBottom: 4,
      wordBreak: "break-word",
    },
    radioRoot: {
      color: theme.palette.text.secondary + " !important",
    },
    statusPill: {
      borderRadius: 8,
      padding: "2px 8px",
      fontSize: "12px",
      textAlign: "center",
      fontWeight: 600,
      minWidth: 70,
      display: "inline-block",
      width: "fit-content",
    },
  })
);

interface Props {
  open: boolean;
  onClose: () => void;
  locationId: string;
  chargebeeCustomerId: string | null;
  onUpdate: () => void;
}

interface DuplicateLicenseDetails {
  licenseCode?: string;
  locationId?: string;
  businessDisplayName?: string;
  deviceType?: string;
  name?: string;
  status?: string;
}

const generateDeviceId = (): string => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let id = "DEV-";
  for (let i = 0; i < 8; i++) {
    id += chars[Math.floor(Math.random() * chars.length)];
  }
  return "DEV-" + Date.now().toString(36).toUpperCase();
};

const getToken = () => Cookies.get("idToken") || null;

const validateLicenseCode = (value: string): string => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "";
  }

  const validPattern = /^[A-Za-z0-9_-]+$/;

  if (!validPattern.test(trimmedValue)) {
    return "License code can only contain letters, numbers, hyphen, and underscore.";
  }

  return "";
};

const LicenseCreateModal: React.FC<Props> = ({
  open,
  onClose,
  locationId,
  chargebeeCustomerId,
  onUpdate,
}) => {
  const classes = useStyles();

  const [name, setName] = useState("");
  const [amount, setAmount] = useState("0.00");
  const [period, setPeriod] = useState("monthly");
  const [status, setStatus] = useState("active");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [subscriptions, setSubscriptions] = useState<ChargebeeSubscription[]>(
    []
  );
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState("");
  const [expandedSubscriptionId, setExpandedSubscriptionId] = useState("");
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptionsError, setSubscriptionsError] = useState("");
  const [locationsById, setLocationsById] = useState<Record<string, string>>({});
  const [deviceType, setDeviceType] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [licenseCode, setLicenseCode] = useState("");
  const [licenseCodeError, setLicenseCodeError] = useState("");
  const [formError, setFormError] = useState("");
  const [duplicateLicense, setDuplicateLicense] =
    useState<DuplicateLicenseDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const hasChargebeeCustomer = Boolean(chargebeeCustomerId);
  const selectedSubscription = subscriptions.find(
    (subscription) => subscription.id === selectedSubscriptionId
  );

  const isFormValid = (): boolean => {
    return (
      name.trim() !== "" &&
      deviceType.trim() !== "" &&
      amount.trim() !== "" &&
      !licenseCodeError
    );
  };

  useEffect(() => {
    if (open) {
      setLicenseCode("");
      setLicenseCodeError("");
      setFormError("");
      setDuplicateLicense(null);
      setName("");
      setAmount("0.00");
      setPeriod("monthly");
      setStatus("active");
      setSubscriptionId("");
      setSubscriptions([]);
      setSelectedSubscriptionId("");
      setExpandedSubscriptionId("");
      setSubscriptionsError("");
      setDeviceType("");
      setDeviceId("");
    }
  }, [open]);

  useEffect(() => {
    if (!open || !chargebeeCustomerId) return;

    const loadSubscriptions = async () => {
      setSubscriptionsLoading(true);
      setSubscriptionsError("");

      try {
        const token = await getToken();

        if (!token) {
          throw new Error("Missing Firebase token");
        }

        const response = await getChargebeeSubscriptionsByCustomer(
          locationId,
          token
        );

        const rawList =
          response?.data?.data?.list ||
          response?.data?.list ||
          response?.data?.data?.subscriptions ||
          [];

        const list = rawList
          .map((item: any) => item.subscription || item)
          .filter((subscription: any) => subscription && subscription.id);

        setSubscriptions(list);

        if (list.length === 0) {
          setSubscriptionsError(
            "No subscriptions found for this Chargebee customer."
          );
        }
      } catch (err) {
        console.error("Error fetching Chargebee subscriptions →", err);
        setSubscriptions([]);
        setSubscriptionsError("Failed to load Chargebee subscriptions.");
      } finally {
        setSubscriptionsLoading(false);
      }
    };

    loadSubscriptions();
  }, [open, chargebeeCustomerId]);

  useEffect(() => {
    if (!open) return;

    const loadLocations = async () => {
      try {
        const token = await getToken();

        if (!token) {
          throw new Error("Missing Firebase token");
        }

        const response = await getAllLocations(token);
        const locationList = response?.data?.data || response?.data || [];

        const locationMap = locationList.reduce(
          (acc: Record<string, string>, location: any) => {
            if (location?.id) {
              acc[location.id] =
                location.businessDisplayName ||
                location.locationIdentifier ||
                location.id;
            }

            return acc;
          },
          {}
        );

        setLocationsById(locationMap);
      } catch (err) {
        console.error("Error fetching locations →", err);
        setLocationsById({});
      }
    };

    loadLocations();
  }, [open]);

  useEffect(() => {
    if (!hasChargebeeCustomer) return;

    if (!selectedSubscription) return;

    const mappedStatus = getLicenseStatusFromSubscriptionStatus(
      selectedSubscription.status
    );

    if (mappedStatus) {
      setStatus(mappedStatus);
    }
  }, [hasChargebeeCustomer, selectedSubscription]);

  const handleLicenseCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = e.target.value;

    setLicenseCode(nextValue);
    setLicenseCodeError(validateLicenseCode(nextValue));
    setFormError("");
    setDuplicateLicense(null);
  };

  const handleSave = async () => {
    const finalLicenseCode = licenseCode.trim();
    const finalLicenseCodeError = validateLicenseCode(finalLicenseCode);

    if (finalLicenseCodeError) {
      setLicenseCodeError(finalLicenseCodeError);
      return;
    }

    const resolvedSubscriptionId = hasChargebeeCustomer
      ? selectedSubscriptionId.trim()
      : subscriptionId.trim();

    const resolvedSubscriptionStatus = selectedSubscription
      ? getLicenseStatusFromSubscriptionStatus(selectedSubscription.status)
      : null;

    const resolvedStatus = hasChargebeeCustomer
      ? resolvedSubscriptionStatus === "locked"
        ? "locked"
        : resolvedSubscriptionStatus || "locked"
      : resolvedSubscriptionId
      ? status
      : "locked";

    setIsLoading(true);
    setFormError("");
    setDuplicateLicense(null);

    try {
      const token = await getToken();

      if (!token) {
        throw new Error("Missing Firebase token");
      }

      const formattedAmount = parseFloat(amount).toFixed(2);

      const payload: any = {
        name: name.trim(),
        subscriptionAmount: formattedAmount,
        subscriptionPeriod: period,
        status: resolvedStatus,
        subscriptionId: resolvedSubscriptionId || undefined,
        deviceType,
        deviceId,
      };

      if (finalLicenseCode) {
        payload.licenseCode = finalLicenseCode;
      }

      await createLicense(locationId, payload, token);

      onUpdate();
      onClose();
    } catch (err) {
      const error = err as any;

      console.error("POST create license error →", error);

      const responseBody = error?.response?.data || {};
      const message =
        responseBody?.message ||
        error?.message ||
        "Failed to create license. Please try again.";

      const duplicateDetails =
        responseBody?.data ||
        responseBody?.details ||
        responseBody?.error?.data ||
        null;

      const isDuplicateLicenseError =
        String(message).toLowerCase().includes("already in use") ||
        Boolean(duplicateDetails?.licenseCode);

      if (isDuplicateLicenseError) {
        const duplicateLocationId = duplicateDetails?.locationId || locationId;

        setDuplicateLicense({
          licenseCode: duplicateDetails?.licenseCode || finalLicenseCode,
          locationId: duplicateLocationId || "Unknown",
          businessDisplayName:
            duplicateDetails?.businessDisplayName ||
            (duplicateLocationId ? locationsById[duplicateLocationId] : "") ||
            "Unknown",
          deviceType: duplicateDetails?.deviceType || "Unknown",
          name: duplicateDetails?.name || "Unknown",
          status: duplicateDetails?.status || "Unknown",
        });

        setFormError("");
      } else {
        setFormError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>Create New License</DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
          <TextfieldDefault
            id="license-code"
            name="license-code"
            type="text"
            label="License Code"
            value={licenseCode}
            onChange={handleLicenseCodeChange}
            style={{ width: "100%" }}
          />

          <Typography className={classes.helperText}>
            Leave empty to auto-generate a license code.
          </Typography>

          {licenseCodeError && (
            <Typography className={classes.errorText}>
              {licenseCodeError}
            </Typography>
          )}

          {duplicateLicense && (
            <Box className={classes.duplicateErrorBox}>
              <Typography className={classes.duplicateErrorTitle}>
                This license code is already in use.
              </Typography>

              <Typography className={classes.duplicateDetailText}>
                <strong>License Code:</strong>{" "}
                {duplicateLicense.licenseCode || "-"}
              </Typography>

              <Typography className={classes.duplicateDetailText}>
                <strong>Location:</strong>{" "}
                {duplicateLicense.businessDisplayName || duplicateLicense.locationId || "-"}
              </Typography>

              <Typography className={classes.duplicateDetailText}>
                <strong>Device Type:</strong>{" "}
                {duplicateLicense.deviceType || "-"}
              </Typography>

              <Typography className={classes.duplicateDetailText}>
                <strong>Created Name:</strong> {duplicateLicense.name || "-"}
              </Typography>

              <Typography className={classes.duplicateDetailText}>
                <strong>Status:</strong> {duplicateLicense.status || "-"}
              </Typography>
            </Box>
          )}

          <TextfieldDefault
            id="license-name"
            name="license-name"
            type="text"
            label="Name"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setName(e.target.value)
            }
            style={{ width: "100%" }}
          />

          <TextField
            select
            label="Device Type"
            variant="outlined"
            fullWidth
            value={deviceType}
            onChange={(e) => setDeviceType(e.target.value)}
            className={classes.dropdown}
            SelectProps={{
              MenuProps: { classes: { paper: classes.dropdownMenuPaper } },
            }}
          >
            {[
              "POS Terminal",
              "Handheld Terminal",
              "Fullsize Kiosk",
              "Tablet Kiosk",
              "KDS",
              "Printer Server",
              "Online Ordering",
              "Table Booking",
              "Inventory",
              "Clock-In",
              "Loyalty",
              "Next-Drop",
            ].map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>

          {hasChargebeeCustomer ? (
            <Box>
              <Typography style={{ marginBottom: 8, fontWeight: 600 }}>
                Select a Chargebee subscription
              </Typography>

              {subscriptionsLoading ? (
                <Typography>Loading subscriptions...</Typography>
              ) : subscriptionsError ? (
                <Typography className={classes.errorText}>
                  {subscriptionsError}
                </Typography>
              ) : (
                <ChargebeeSubscriptionList
                  subscriptions={subscriptions}
                  selectedSubscriptionId={selectedSubscriptionId}
                  expandedSubscriptionId={expandedSubscriptionId}
                  isFetching={subscriptionsLoading}
                  onSelectSubscription={setSelectedSubscriptionId}
                  onToggleExpanded={(subscriptionId) =>
                    setExpandedSubscriptionId((current) =>
                      current === subscriptionId ? "" : subscriptionId
                    )
                  }
                />
              )}
            </Box>
          ) : (
            <TextfieldDefault
              id="subscription-id"
              name="subscription-id"
              type="text"
              label="Subscription ID"
              value={subscriptionId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSubscriptionId(e.target.value)
              }
              style={{ width: "100%" }}
            />
          )}

          <input type="hidden" value={amount} readOnly />
          <input type="hidden" value={period} readOnly />

          {/* Status*/}

          {formError && (
            <Typography className={classes.errorText}>{formError}</Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <ButtonCommon
          variant="contained"
          color="orange"
          onClick={onClose}
          style={{ minWidth: 120, height: 36 }}
        >
          Cancel
        </ButtonCommon>

        <ButtonCommon
          variant="contained"
          color="green"
          onClick={handleSave}
          isLoadingPage={isLoading}
          isDisabled={!isFormValid() || isLoading}
          style={{ minWidth: 120, height: 36 }}
        >
          Save
        </ButtonCommon>
      </DialogActions>
    </Dialog>
  );
};

export default LicenseCreateModal;
